import {closeCurrentDialog, openDialog} from 'platform/components';

import {useRef} from 'react';

import i18n from '@dms/i18n';

import {useOnMount} from 'shared/web';

import {PhotoDownloadSizeDialog} from '../components/PhotoDownloadSizeDialog/PhotoDownloadSizeDialog';
import {PhotoDownloadSizePreset} from '../constants/photoDownloadSizePresets';
import {usePhotoDownloadSizePreset} from './usePhotoDownloadSizePreset';

const PHOTO_DOWNLOAD_SIZE_DIALOG_ID = 'photo-download-size-dialog';

type SettlePhotoDownloadSizeDialog = (preset: PhotoDownloadSizePreset | null) => void;

/**
 * Settles the promise of the open dialog. Platform dialogs drop a dialog without `onCloseComplete`
 * when a dialog with the same id opens or the route changes, so a new open settles the old promise.
 */
let settlePendingDialog: SettlePhotoDownloadSizeDialog | null = null;

/**
 * Dialog content key. The platform keeps the dialog instance while it closes, so a fast reopen
 * reuses it; a new key per open discards the unconfirmed selection of the previous open.
 */
let dialogOpenCount = 0;

/**
 * Asks the user for the maximum size of every downloaded photo. The dialog pre-selects the size
 * chosen last in this browser and remembers the confirmed one.
 */
export function usePhotoDownloadSizeDialog() {
  const [rememberedPreset, setRememberedPreset] = usePhotoDownloadSizePreset();
  const ownSettleRef = useRef<SettlePhotoDownloadSizeDialog | null>(null);

  // Effect cleanup is the only unmount signal; it settles a promise left open by this hook owner.
  useOnMount(undefined, () => ownSettleRef.current?.(null));

  /** Resolves to the confirmed preset, or `null` when the dialog closes without a confirmation. */
  const openPhotoDownloadSizeDialog = () =>
    new Promise<PhotoDownloadSizePreset | null>((resolve) => {
      const settle: SettlePhotoDownloadSizeDialog = (preset) => {
        if (settlePendingDialog === settle) {
          settlePendingDialog = null;
        }
        resolve(preset);
      };

      settlePendingDialog?.(null);
      settlePendingDialog = settle;
      ownSettleRef.current = settle;
      dialogOpenCount += 1;

      openDialog(
        <PhotoDownloadSizeDialog
          key={dialogOpenCount}
          defaultPreset={rememberedPreset}
          onConfirm={(preset) => {
            setRememberedPreset(preset);
            settle(preset);
            closeCurrentDialog();
          }}
          onCancel={closeCurrentDialog}
          data-testid={PHOTO_DOWNLOAD_SIZE_DIALOG_ID}
        />,
        {
          id: PHOTO_DOWNLOAD_SIZE_DIALOG_ID,
          title: i18n.t('entity.photo.labels.downloadSize'),
          size: 'small',
          withAdditionalFooter: true,
          // Settles a dialog closed without confirming; a no-op once onConfirm resolved it.
          onCloseComplete: () => settle(null),
          'data-testid': PHOTO_DOWNLOAD_SIZE_DIALOG_ID,
        }
      );
    });

  return {openPhotoDownloadSizeDialog};
}
